import { mat2d, type ReadonlyMat2d, vec2, type ReadonlyVec2 } from 'gl-matrix';

import { type ColorName } from '@canvas/constants/colors/color-names';
import { getColorCSSValue } from '@canvas/constants/get-color-value';

import { GraphicStrokeColor, GraphicFillColor } from './color.ts';
import { GraphicSolidColor, type Graphic } from './graphic.ts';
import { rotateAroundPointMatrix } from './math.ts';
import {
  GraphicCubicBezier,
  GraphicLine,
  type GraphicSection,
  type GraphicSegment,
  GraphicSubPathStart,
} from './types.ts';
export interface FillPathData {
  /**
   * The SVG path data string for the fill
   */
  path: string;
  /**
   * The optional color of this fill as a string. Represents an RGB string if
   * the `GraphicFill` contains a `GraphicSolidColor`, which it must always use
   * as its color.
   */
  color?: string;
}

const pointFlyweight = vec2.create();
const controlPoint1Flyweight = vec2.create();
const controlPoint2Flyweight = vec2.create();

const transformFlyweight = mat2d.create();

/**
 * Converts a fill (segment(s)) to SVG path data string
 * @param segments Array of segments with start, end, and optional control points
 * @param size The size of the graphic in world units
 * @param rotation The rotation of the graphic in radians (optional)
 * @param relativeRotationOrigin The origin point of the rotation in world scale,
 * relative to the top left of the graphic (optional)
 * @returns SVG path data string or empty string if no segments
 */
export function fillToSVGPathData(
  segments: readonly GraphicSegment[],
  size: ReadonlyVec2,
  rotation?: number,
  relativeRotationOrigin?: ReadonlyVec2,
): string {
  if (segments.length === 0) {
    return '';
  }

  if (rotation && relativeRotationOrigin) {
    rotateAroundPointMatrix(relativeRotationOrigin, rotation, transformFlyweight);
  } else {
    mat2d.identity(transformFlyweight);
  }

  // Move to the start of the first segment
  const firstSegment = segments[0];
  const start = firstSegment.start.resolveToWorldScaleWithTransform(
    pointFlyweight,
    size,
    transformFlyweight,
  );

  let pathData = `M ${start[0]} ${start[1]} `;

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    pathData += convertSegmentToPathData(segment, size, transformFlyweight);
  }

  pathData += 'Z';

  return pathData;
}

/**
 * Converts a stroke (segment(s)) to SVG path data string
 * @param segments Array of segments with start, end, and optional control points
 * @param size The size of the graphic in world units
 * @param rotation The rotation of the graphic in radians (optional)
 * @param relativeRotationOrigin The origin point of the rotation in world scale,
 * relative to the top left of the graphic (optional)
 * @returns SVG path data string or empty string if no segments
 */
function strokeToSVGPathData(
  segments: readonly GraphicSegment[],
  size: ReadonlyVec2,
  rotation?: number,
  relativeRotationOrigin?: ReadonlyVec2,
): string {
  if (segments.length === 0) {
    return '';
  }

  if (rotation && relativeRotationOrigin) {
    rotateAroundPointMatrix(relativeRotationOrigin, rotation, transformFlyweight);
  } else {
    mat2d.identity(transformFlyweight);
  }

  const firstSegment = segments[0];
  const startPoint = firstSegment.start.resolveToWorldScaleWithTransform(
    pointFlyweight,
    size,
    transformFlyweight,
  );

  let pathData = `M ${startPoint[0]} ${startPoint[1]} `;

  for (const segment of segments) {
    pathData += convertSegmentToPathData(segment, size, transformFlyweight);
  }

  pathData = pathData.trim();

  // Note: No 'Z' for strokes as they don't need to be closed

  return pathData;
}

function convertSegmentToPathData(
  segment: GraphicSegment,
  size: ReadonlyVec2,
  transform: ReadonlyMat2d,
) {
  if (segment instanceof GraphicCubicBezier) {
    const control1 = segment.control1.resolveToWorldScaleWithTransform(
      controlPoint1Flyweight,
      size,
      transform,
    );
    const control2 = segment.control2.resolveToWorldScaleWithTransform(
      controlPoint2Flyweight,
      size,
      transform,
    );
    const end = segment.end.resolveToWorldScaleWithTransform(
      pointFlyweight,
      size,
      transform,
    );
    return `C ${control1[0]} ${control1[1]}, ${control2[0]} ${control2[1]}, ${end[0]} ${end[1]} `;
  } else if (segment instanceof GraphicLine) {
    const end = segment.end.resolveToWorldScaleWithTransform(
      pointFlyweight,
      size,
      transform,
    );
    return `L ${end[0]} ${end[1]} `;
  } else if (segment instanceof GraphicSubPathStart) {
    const start = segment.start.resolveToWorldScaleWithTransform(
      pointFlyweight,
      size,
      transform,
    );
    return `M ${start[0]} ${start[1]} `;
  }

  return '';
}

export function graphicFillToPath2D(
  fill: GraphicSection,
  size: ReadonlyVec2,
  rotation?: number,
  relativeRotationOrigin?: ReadonlyVec2,
): Path2D | null {
  const svgPathData = fillToSVGPathData(
    fill.segments,
    size,
    rotation,
    relativeRotationOrigin,
  );

  if (!svgPathData) {
    return null;
  }

  // A guard for test environments that don't support Path2D or don't have any
  // reason to mock it.
  if (!globalThis.Path2D) {
    return null;
  }

  return new Path2D(svgPathData);
}

/**
 * Extracts a solid color from a GraphicSection as an RGB CSS string
 */
function extractSolidColorAsRGB(section: GraphicSection): string | undefined {
  return section.color instanceof GraphicSolidColor
    ? `rgb(${section.color.color[0]}, ${section.color.color[1]}, ${section.color.color[2]})`
    : undefined;
}

/**
 * Converts all fills in a graphic to SVG path data array
 *
 * @param graphic The graphic to convert fills from
 * @param size Scales the points by a vec2 before turning them into path data
 * @param strokeColor Optional stroke color to use for fills with GraphicStrokeColor
 * @returns Array of fill path data objects containing the path string and optional color
 */
export function graphicFillsToPathDataArray(
  graphic: Graphic,
  size: ReadonlyVec2,
  strokeColor?: ColorName,
): FillPathData[] {
  return graphic.fills.map((fill) => ({
    path: fillToSVGPathData(fill.segments, size),
    color:
      fill.color instanceof GraphicStrokeColor && strokeColor !== undefined
        ? getColorCSSValue(strokeColor)
        : extractSolidColorAsRGB(fill),
  }));
}

export interface StrokePathData {
  path: string;
  color?: string;
}

/**
 * Converts all strokes in a graphic to SVG path data array
 *
 * @param graphic The graphic to convert strokes from
 * @param size Scales the points by a vec2 before turning them into path data
 * @param fillColor Optional fill color to use for strokes with GraphicStrokeColor
 * @returns Array of stroke path data objects containing the path string and optional color
 */
export function graphicStrokesToPathDataArray(
  graphic: Graphic,
  size: ReadonlyVec2,
  fillColor?: string,
): StrokePathData[] {
  return graphic.strokes.map((stroke) => {
    return {
      path: strokeToSVGPathData(stroke.segments, size),
      color:
        stroke.color instanceof GraphicFillColor && fillColor !== undefined
          ? fillColor
          : extractSolidColorAsRGB(stroke),
    };
  });
}
